import { ApiService } from "api/api.service";
import { Pagination, Skeleton, Tooltip, Typography } from "antd";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { T_DesktopInbox } from "dtos/communication-channel.dto";
import { timeAgo } from "utils/functions";
import OutgoingMails from "components/svgs/Outbox";
import { TbExternalLink, TbX } from "react-icons/tb";
import { MdOutlineKeyboardDoubleArrowLeft } from "react-icons/md";

const SearchIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <circle cx="5" cy="5" r="3.5" stroke="#9ca3af" strokeWidth="1.2" />
    <path d="M8 8l2.5 2.5" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const PAGE_SIZE = 20;

const AVATAR_PALETTE: Array<{ bg: string; fg: string }> = [
  { bg: "#e0e7ff", fg: "#4338ca" },
  { bg: "#dcfce7", fg: "#15803d" },
  { bg: "#fce7f3", fg: "#9d174d" },
  { bg: "#fef3c7", fg: "#92400e" },
  { bg: "#e0f2fe", fg: "#0369a1" },
  { bg: "#f3e8ff", fg: "#7e22ce" },
];

export function getAvatarColor(name: string): { bg: string; fg: string } {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

type T_EnrichedItem = {
  _id: string;
  personId: string;
  name: string;
  initials: string;
  time: string;
  snippet: string;
  subject: string | null;
  channelLabel: string;
  channelBg: string;
  channelColor: string;
  unread: boolean;
  selectedBg: string;
  selectedBorderColor: string;
  avatarBg: string;
  avatarFg: string;
  nameWeight: number;
  nameColor: string;
  appKey: string;
};

function enrichInboxItem(item: T_DesktopInbox, selectedPersonId: string): T_EnrichedItem {
  const name = item.person?.meta?.name || "Unknown";
  const isSelected = item.person?._id === selectedPersonId;
  const avatar = getAvatarColor(name);
  const isEmail = String(item.lastMessage?.type ?? "")
    .toLowerCase()
    .includes("email");

  return {
    _id: item._id,
    personId: item.person?._id,
    name,
    initials: getInitials(name),
    time: item.lastMessage?.deliveredAt ? timeAgo(item.lastMessage.deliveredAt) : "",
    snippet: item.lastMessage?.snippet ?? "",
    subject: item.lastMessage?.subject ?? null,
    channelLabel: isEmail ? "Email" : "SMS",
    channelBg: isEmail ? "#eef2ff" : "#ecfdf5",
    channelColor: isEmail ? "#4f46e5" : "#059669",
    // T_DesktopInbox has no unread field; revisit when the API exposes it
    unread: false,
    selectedBg: isSelected ? "#f0f4ff" : "#f8f9fc",
    selectedBorderColor: isSelected ? "#6366f1" : "transparent",
    avatarBg: avatar.bg,
    avatarFg: avatar.fg,
    nameWeight: isSelected ? 600 : 500,
    nameColor: isSelected ? "#111827" : "#374151",
    appKey: item.person.correlation_id,
  };
}

type T_ThreadRowProps = {
  thread: T_EnrichedItem;
  onSelect: () => void;
};

const ThreadRow: React.FC<T_ThreadRowProps> = ({ thread, onSelect }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "11px 14px",
        borderBottom: "1px solid #f0f0f8",
        cursor: "pointer",
        background: hovered ? "#fff" : thread.selectedBg,
        borderLeft: `2px solid ${thread.selectedBorderColor}`,
        transition: "background 0.1s",
      }}
    >
      <div className="flex items-start gap-[10px]">
        {/* Avatar */}
        <div className="relative shrink-0">
          <div
            className="flex h-[37px] w-[37px] items-center justify-center rounded-full"
            style={{ background: thread.avatarBg }}
          >
            <span className="text-xs font-semibold" style={{ color: thread.avatarFg }}>
              {thread.initials}
            </span>
          </div>
          {thread.unread && (
            <div
              className="absolute -top-px -right-px h-[9px] w-[9px] rounded-full bg-red-500"
              style={{ border: "1.5px solid #f8f9fc" }}
            />
          )}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="mb-[2px] flex items-center justify-between">
            <div className="flex gap-1 items-center">
              <span
                className="max-w-[150px] truncate text-[12px]"
                style={{ fontWeight: thread.nameWeight, color: thread.nameColor }}
              >
                {thread.name}
              </span>
              <span className="max-w-[150px] truncate text-[10px] ">{`(${thread.appKey})`}</span>
            </div>
            <div className="flex items-center gap-1">
              {thread.time && <span className="ml-1 shrink-0 text-[10px] text-gray-400">{thread.time} ago</span>}
              {/* <TbExternalLink
                onClick={() => window.open(`/dashboard/application/${thread.appKey}`)}
                className="cursor-pointer text-gray-400"
                size={12}
              /> */}
            </div>
          </div>

          {/* Channel badge + subject */}
          <div className="mb-[3px] flex items-center gap-[5px]">
            <span
              className="shrink-0 rounded-[3px] px-[5px] py-[1px] text-[9.5px] font-bold uppercase tracking-[0.4px]"
              style={{ background: thread.channelBg, color: thread.channelColor }}
            >
              {thread?.channelLabel}
            </span>
            {thread.subject && (
              <span className="truncate text-[11.5px] font-medium text-gray-700">{thread.subject}</span>
            )}
          </div>

          {thread.snippet && <p className="truncate text-[11.5px] leading-[1.4] text-gray-500">{thread.snippet}</p>}
        </div>
      </div>
    </div>
  );
};

const PersonList: React.FC<{ deskId: string | undefined; isOpen: boolean; closePanel: () => void }> = ({
  deskId,
  isOpen,
  closePanel,
}) => {
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const selectedPersonId = searchParams.get("person_id") ?? "";
  const orgCode = searchParams.get("orgCode");

  // NOTE: `useGetInboxOfDesk` hook needs to be updated to:
  //   1. accept `page: number` and `limit: number` params
  //   2. include them in the queryKey → ["desks", desk_id, "inbox", page, limit]
  //   3. forward them as query params → `/desks/${desk_id}/inbox?page=N&limit=N`
  const { data, isLoading } = ApiService.communicationChannel.useGetInboxOfDesk({
    desk_id: deskId,
    page,
    limit: PAGE_SIZE,
    search,
  });
  const orgDetailsRes = ApiService.organisation.useGetBySlug(orgCode as string | undefined);
  const orgDetails = orgDetailsRes.data;
  const items = data?.result?.data ?? [];
  const total = data?.result?.totalObjects ?? 0;

  const enriched = items.map((item) => enrichInboxItem(item, selectedPersonId));

  const handleSelectThread = (personId: string) => {
    setSearchParams((prev) => {
      prev.set("person_id", personId);
      return prev;
    });
  };

  return (
    <div
      className="flex shrink-0 flex-col"
      style={{
        width: isOpen ? 320 : 0,
        minWidth: isOpen ? 320 : 0,
        transition: "width 0.2s ease, min-width 0.2s ease",
        borderRight: "1px solid #e5e7eb",
        borderRadius: 8,
        background: "#f8f9fc",
      }}
    >
      {/* ── Header ── */}
      <div className="bg-white px-[14px] pt-[14px] pb-[10px]" style={{ borderBottom: "1px solid #f0f0f8" }}>
        <div className="mb-[11px] flex items-center justify-between">
          <span className="text-[15px] font-semibold tracking-[-0.2px] text-gray-900">Inbox</span>
          {selectedPersonId && (
            <Tooltip title="Close panel">
              <MdOutlineKeyboardDoubleArrowLeft onClick={closePanel} className="cursor-pointer" />
            </Tooltip>
          )}
        </div>

        {/* Search */}
        <div
          className="mb-[10px] flex items-center gap-2 rounded-[8px] bg-gray-100 px-[10px] py-[7px]"
          style={{ border: "1px solid #e5e7eb" }}
        >
          <SearchIcon />
          <input
            value={search}
            type="text"
            placeholder="Search conversations…"
            className="flex-1 border-none bg-transparent text-[12.5px] text-gray-900 outline-none"
            onChange={(e) => setSearch(e.target.value)}
          />
          {search !== "" && <TbX className="cursor-pointer" onClick={() => setSearch("")} />}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {isLoading ? (
          <div className="space-y-3 p-[14px]">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} avatar active paragraph={{ rows: 2 }} />
            ))}
          </div>
        ) : enriched.length === 0 ? (
          <div>
            <OutgoingMails title="No Chats Found" />
          </div>
        ) : (
          enriched.map((thread) => (
            <ThreadRow key={thread._id} thread={thread} onSelect={() => handleSelectThread(thread.personId)} />
          ))
        )}
      </div>

      {/* ── Pagination ── */}
      {(data?.result.totalPages ?? 0) > 1 && (
        <div
          className="flex justify-center px-[14px] py-[10px]"
          style={{ borderTop: "1px solid #f0f0f8", background: "#fff" }}
        >
          <Pagination
            current={page}
            total={total}
            pageSize={PAGE_SIZE}
            onChange={(p) => setPage(p)}
            size="small"
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default PersonList;
